import React, { useEffect, useRef } from "react";

const PARTICLE_COUNT = 90;
const DPR_LIMIT = 1.5;

function createParticles(width, height, particleCount) {
  const orbitCount = Math.floor(particleCount * 0.55);
  const fieldCount = particleCount - orbitCount;

  const orbitParticles = Array.from({ length: orbitCount }, (_, index) => ({
    type: "orbit",
    angle: (Math.PI * 2 * index) / orbitCount,
    radius: Math.max(width, height) * (0.08 + Math.random() * 0.34),
    speed: 0.0006 + Math.random() * 0.0014,
    size: 0.7 + Math.random() * 1.8,
    alpha: 0.28 + Math.random() * 0.46,
  }));

  const fieldParticles = Array.from({ length: fieldCount }, () => ({
    type: "field",
    x: Math.random() * width,
    y: Math.random() * height,
    drift: 0.05 + Math.random() * 0.14,
    size: 0.45 + Math.random() * 1.7,
    alpha: 0.16 + Math.random() * 0.42,
    twinkle: Math.random() * Math.PI * 2,
  }));

  return [...orbitParticles, ...fieldParticles];
}

function drawPyramid(ctx, cx, cy, scale, time) {
  const topY = cy - scale * 0.95;
  const baseY = cy + scale * 0.75;
  const leftX = cx - scale * 0.95;
  const rightX = cx + scale * 0.95;
  const pulse = Math.sin(time * 0.0018) * 0.08 + 0.92;

  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  ctx.lineWidth = 1.3;
  ctx.strokeStyle = `rgba(53, 214, 255, ${0.34 * pulse})`;
  ctx.shadowBlur = 18;
  ctx.shadowColor = "rgba(53, 214, 255, 0.42)";

  ctx.beginPath();
  ctx.moveTo(cx, topY);
  ctx.lineTo(leftX, baseY);
  ctx.lineTo(rightX, baseY);
  ctx.closePath();
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(cx, topY);
  ctx.lineTo(cx, baseY);
  ctx.moveTo(leftX, baseY);
  ctx.lineTo(cx, cy + scale * 0.06);
  ctx.lineTo(rightX, baseY);
  ctx.stroke();

  for (let i = 1; i <= 4; i += 1) {
    const y = topY + ((baseY - topY) / 5) * i;
    const inset = ((y - topY) / (baseY - topY)) * scale * 0.95;
    ctx.globalAlpha = 0.13;
    ctx.beginPath();
    ctx.moveTo(cx - inset, y);
    ctx.lineTo(cx + inset, y);
    ctx.stroke();
  }

  ctx.restore();
}

function drawEye(ctx, cx, cy, scale, time) {
  const glow = Math.sin(time * 0.002) * 0.12 + 0.88;

  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  ctx.translate(cx, cy - scale * 0.1);
  ctx.scale(1, 0.46);
  ctx.lineWidth = 2;
  ctx.strokeStyle = `rgba(231, 238, 248, ${0.56 * glow})`;
  ctx.shadowBlur = 28;
  ctx.shadowColor = "rgba(53, 214, 255, 0.8)";
  ctx.beginPath();
  ctx.arc(0, 0, scale * 0.28, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  const iris = ctx.createRadialGradient(cx, cy - scale * 0.1, 0, cx, cy - scale * 0.1, scale * 0.1);
  iris.addColorStop(0, "rgba(255, 255, 255, 0.92)");
  iris.addColorStop(0.35, "rgba(53, 214, 255, 0.72)");
  iris.addColorStop(1, "rgba(11, 75, 255, 0)");

  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  ctx.fillStyle = iris;
  ctx.beginPath();
  ctx.arc(cx, cy - scale * 0.1, scale * 0.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function CosmicPyramidCanvas({
  className = "",
  isVisiblePramid = false,
  focusX = 0.5,
  focusY = 0.48,
  particleCount = PARTICLE_COUNT,
  visualScale = 1,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = canvas.getContext("2d", { alpha: true });
    let frameId;
    let isVisible = true;
    let width = 0;
    let height = 0;
    let dpr = 1;
    let particles = [];

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, DPR_LIMIT);
      width = Math.max(1, Math.floor(rect.width));
      height = Math.max(1, Math.floor(rect.height));
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      particles = createParticles(width, height, particleCount);
    };

    const render = (time = 0) => {
      ctx.clearRect(0, 0, width, height);

      const cx = width * focusX;
      const cy = height * focusY;
      const scale = Math.min(width, height) * 0.28 * visualScale;

      const voidGlow = ctx.createRadialGradient(cx, cy, scale * 0.1, cx, cy, Math.max(width, height) * 0.58);
      voidGlow.addColorStop(0, "rgba(53, 214, 255, 0.24)");
      voidGlow.addColorStop(0.34, "rgba(9, 16, 38, 0.2)");
      voidGlow.addColorStop(1, "rgba(5, 8, 20, 0)");
      ctx.fillStyle = voidGlow;
      ctx.fillRect(0, 0, width, height);

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(time * 0.00008);
      particles.forEach((particle) => {
        if (particle.type !== "orbit") return;

        const angle = particle.angle + time * particle.speed;
        const wave = Math.sin(time * 0.001 + particle.radius * 0.02) * 18;
        const x = Math.cos(angle) * (particle.radius + wave);
        const y = Math.sin(angle) * (particle.radius * 0.48 + wave * 0.18);

        ctx.globalAlpha = particle.alpha;
        ctx.fillStyle = "rgba(231, 238, 248, 0.92)";
        ctx.beginPath();
        ctx.arc(x, y, particle.size, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();

      particles.forEach((particle) => {
        if (particle.type !== "field") return;

        const y = (particle.y + time * particle.drift * 0.02) % height;
        const twinkle = Math.sin(time * 0.002 + particle.twinkle) * 0.22 + 0.78;
        ctx.globalAlpha = particle.alpha * twinkle;
        ctx.fillStyle = "rgba(231, 238, 248, 0.84)";
        ctx.beginPath();
        ctx.arc(particle.x, y, particle.size, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;

      for (let i = 0; i < 3; i += 1) {
        ctx.save();
        ctx.globalAlpha = 0.16 - i * 0.035;
        ctx.strokeStyle = i === 1 ? "rgba(216, 183, 106, 0.58)" : "rgba(53, 214, 255, 0.52)";
        ctx.lineWidth = 1;
        ctx.translate(cx, cy);
        ctx.rotate(time * (0.00012 + i * 0.00005));
        ctx.scale(1, 0.46 + i * 0.08);
        ctx.beginPath();
        ctx.arc(0, 0, scale * (1.15 + i * 0.28), 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      if (isVisiblePramid) {
        drawPyramid(ctx, cx, cy + scale * 0.14, scale, time);
        drawEye(ctx, cx, cy + scale * 0.08, scale, time);
      }

      if (!prefersReducedMotion && isVisible && !document.hidden) {
        frameId = window.requestAnimationFrame(render);
      }
    };

    const start = () => {
      if (!frameId && !prefersReducedMotion && isVisible && !document.hidden) {
        frameId = window.requestAnimationFrame(render);
      }
    };

    const stop = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
        frameId = undefined;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };

    resize();
    render(0);

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
        if (isVisible) {
          start();
        } else {
          stop();
        }
      },
      { threshold: 0.05 }
    );

    observer.observe(canvas);
    start();

    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", handleVisibility);
      stop();
    };
  }, [focusX, focusY, isVisiblePramid, particleCount, visualScale]);

  return (
    <canvas
      ref={canvasRef}
      className={`cosmic-pyramid-canvas ${className}`.trim()}
      aria-hidden="true"
    />
  );
}

export default CosmicPyramidCanvas;
